import { useCallback } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { getDeviceMetrics } from '@marqvision/shared';
import { checkIsClientView, useUserInfoQuery } from '~/apis/auth';
import { useIpBrandListQuery } from '~/apis/portfolio/ipDocuments';
import { useSelectedBrandId } from '~/hooks';

const getNoUnderscoreProps = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return value.replaceAll('_', ' ');
  }
  if (Array.isArray(value)) {
    return value.map((v) => getNoUnderscoreProps(v)) as string[];
  }

  return value;
};

/**
 * 중요) useLogger 수정시 PM 께 공유하고, QA 요청을 해야합니다.
 */
export const useLogger = () => {
  const { brandId } = useSelectedBrandId();
  const { data: clientData } = useUserInfoQuery();
  const { data } = useIpBrandListQuery({ enabled: !!clientData });

  const amplitudeLogging = useCallback(
    (event: string, eventProps: Record<string, unknown> = {}) => {
      const brandList = data?.brand_list || [];
      const userEmail = clientData?.user_email || null;
      const isClientView = checkIsClientView();

      const eventProperties: Record<string, unknown> = {
        Product: clientData?.use_impersonation_only
          ? 'impersonation'
          : clientData?.use_piracy
          ? 'piracy'
          : 'counterfeit',
        By: !isClientView ? 'client' : 'admin',
        'Client Id': clientData?.client_id,
        'Client Name': clientData?.client_name,
        User: userEmail,
        'Primary Type': clientData?.customer_type?.primary,
        'Secondary Type': clientData?.customer_type?.secondary,
        ...getDeviceMetrics(),
      };

      if (brandId) {
        eventProperties['Brand Name'] = brandList.find((brand) => brand.brand_id === brandId)?.name;
      } else {
        eventProperties['Brand Name'] = brandList.map((brand) => brand.name);
      }

      // Replace underscores of properties with spaces
      for (const [key, value] of Object.entries(eventProps)) {
        eventProperties[key] = getNoUnderscoreProps(value);
      }

      amplitude.setUserId(userEmail ?? undefined);
      amplitude.logEvent(event, eventProperties);

      if (import.meta.env.MODE !== 'production') {
        console.log('[Amplitude Log]', event, JSON.stringify(eventProperties, null, 2));
      }
    },
    [brandId, data, clientData]
  );

  return {
    amplitudeLogging,
  };
};
